import { useMemo } from 'react';
import type { SubscriptionBlockViewDto } from '@subscriptions/application/dtos';
import { useSubscriptionBlocks } from '@subscriptions/presentation/hooks/useSubscriptionBlocks';

/** Derives whether at least one block in the list has `configEnabled === false` (config still in progress). */
export const isAnyBlockConfiguring = (blocks?: SubscriptionBlockViewDto[]): boolean =>
  Boolean(blocks?.some((block) => !block.configEnabled));

/**
 * Hook that exposes the async-configuration state across all subscription blocks.
 *
 * Consumes the shared `useSubscriptionBlocks` query (same React Query key, so it
 * dedupes/caches whether called from the list or the detail route) and derives
 * `isAnyConfiguring` so both views can block actions while a backend
 * configuration runs over the involved stores.
 *
 * @returns The blocks list query result plus the derived `isAnyConfiguring` flag.
 *
 * @example
 * const { isAnyConfiguring, refetch } = useBlocksConfiguring();
 */
export const useBlocksConfiguring = () => {
  const query = useSubscriptionBlocks();
  const { data: blocks } = query;

  const isAnyConfiguring = useMemo(() => isAnyBlockConfiguring(blocks), [blocks]);

  return { ...query, isAnyConfiguring };
};
